import { Box, Grid } from "@mui/material";
import { ReactComponent as Bell } from "assets/Bell.svg";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import NoPlansBanner from "components/common/NoPlansBanner";
import SearchAndFilterRow from "components/common/SearchAndFilterRow";
import Spinner from "components/common/Spinner/Spinner";
import Typography from "components/common/Typography";
import Plans from "components/common/plans/plans.layout";
import { useTranslate } from "hooks/useTranslate";
import { isEmpty, uniqueId } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { PAGE_SIZE, accessTypes } from "utils/CommonConstants";
import { triggerOnboadingWizard } from "utils/CommonFunctions";
import {
  fetchSuggestedPlansAsync,
  fetchSuggestedPlansIdAsync,
  removePlans,
  setSuggestedPlanList
} from "./Suggested.slice";
import TopSuggestedPlans from "./TopSuggestedPlans";
import "./index.scss";

function Suggested() {
  const translate = useTranslate();
  const dispatch = useAppDispatch();
  const [pageNo, setPageNo] = useState(1);
  const [isSeeAll, setSeeAll] = useState<boolean>(false);
  const [focusedItem, setFocusedItems] = useState<string[] | []>([]);

  const searchText = useAppSelector((state) => state.app.searchText);
  const [inputText, setInputText] = useState(searchText);
  const [planTypesCmsId, setPlanTypesCmsId] = useState<string[]>([]);
  const plansList: any = useAppSelector((state) => state.suggestedPlans.SuggestedPlansList);
  const listIds: any = useAppSelector((state) => state.suggestedPlans.listIds);
  const suggestedPlanIds: any = useAppSelector((state) => state.suggestedPlans.listIds);
  const selectedFunctionalAreas = useAppSelector((state: any) => state.app.selectedFunctionalAreas);
  const selectedPlanType = useAppSelector((state: any) => state.app.selectedPlanType);
  const totalRecords: any = useAppSelector((state) => state.suggestedPlans.totalRecords);
  const isLoading = useAppSelector((state) => state.suggestedPlans.status);
  const removeInvalidPlan: any = useAppSelector((state) => state.suggestedPlans.removeInvalidPlan);
  const innCode = useAppSelector((state: any) => state.app.innCode);
  const suggestedShowLoader = useAppSelector((state) => state.suggestedPlans.suggestedShowLoader);
  const topSuggestedShowLoader = useAppSelector((state) => state.suggestedPlans.topSuggestedShowLoader);
  const topSuggestedPlanIds: any = useAppSelector((state) => state.suggestedPlans.topSuggestedPlanIds);
  const newAreaOfFocusesAvailable: any = useAppSelector((state) => state.suggestedPlans.newAreaOfFocusesAvailable);
  const selectedFocusAreas: string[] = useAppSelector((state) => state.suggestedPlans.selectedFocusAreas);
  const { isMobileScreen } = useContext(ThemeContext);
  useEffect(() => {
    const getSuggestedPlansIds = async () => {
      await dispatch(fetchSuggestedPlansIdAsync({ innCode, getTopSuggested: true }));
    };
    getSuggestedPlansIds();
  }, [innCode, removeInvalidPlan]);

  useEffect(() => {
    if (selectedPlanType.length) {
      const cmsIds = suggestedPlanIds
        .filter((item: any) => selectedPlanType.includes(item.planType))
        .map((item: any) => item.cmsId);
      setPlanTypesCmsId(cmsIds);
      return;
    }

    const cmsId = suggestedPlanIds?.map((item: any) => item.cmsId);
    setPlanTypesCmsId(cmsId);
  }, [selectedPlanType, suggestedPlanIds]);

  useEffect(() => {
    const props: any = {
      filter: {
        innCode,
        ids: topSuggestedPlanIds.map((item: any) => item.cmsId)
      },
      pagination: {
        page: 1,
        size: 3
      },
      isSortingDisabled: true,
      topSuggested: true
    };
    if (topSuggestedPlanIds.length > 0) dispatch(fetchSuggestedPlansAsync(props));
  }, [dispatch, topSuggestedPlanIds]);

  useEffect(() => {
    return () => {
      dispatch(removePlans());
    };
  }, []);

  const getSuggestedPlans = async (pageNo: any) => {
    const props: any = {
      filter: {
        innCode,
        functionalAreaIds: selectedFunctionalAreas,
        ids: planTypesCmsId
      },
      pagination: {
        page: pageNo,
        size: PAGE_SIZE
      },
      searchText: inputText,
      isSortingDisabled: true
    };
    if (planTypesCmsId.length > 0 && topSuggestedPlanIds.length > 0) dispatch(fetchSuggestedPlansAsync(props));
    else dispatch(setSuggestedPlanList());
  };

  useEffect(() => {
    getSuggestedPlans(1);
    setPageNo(1);
  }, [dispatch, inputText, selectedFunctionalAreas, planTypesCmsId]);

  const fetchMore = () => {
    getSuggestedPlans(pageNo + 1);
    setPageNo(pageNo + 1);
  };

  const getInputDetails = (inputText: string) => {
    if (inputText.length > 2 || !inputText) {
      setInputText(inputText);
    }
  };

  const handleSetSeeAll = () => {
    setSeeAll(!isSeeAll);
  };

  useEffect(() => {
    const subset = selectedFocusAreas?.slice(0, 3);
    if (!isSeeAll) {
      setFocusedItems(subset);
    } else {
      setFocusedItems(selectedFocusAreas);
    }
  }, [isSeeAll, selectedFocusAreas]);

  const focusedAreaText = isMobileScreen
    ? translate("dashboard.focusedArea.flagTrue.mobile")
    : translate("dashboard.focusedArea.flagTrue.desktop");

  return (
    <>
      {suggestedShowLoader || topSuggestedShowLoader ? (
        <Spinner backdropVisible />
      ) : (
        <>
          {topSuggestedPlanIds?.length ? (
            <div className="banner-main">
              <div className="left-container">
                <div className="heading-wrapper">
                  {newAreaOfFocusesAvailable && (
                    <>
                      <Bell />
                      <Typography
                        showBold
                        variant="body1"
                        color={tokens.Icon.General.Inverse}
                        children={focusedAreaText}
                      />
                      <Bell />
                    </>
                  )}
                </div>
                {selectedFocusAreas?.length ? (
                  <>
                    <div className="content-wrapper">
                      <Typography variant="body2" color={tokens.Icon.General.Inverse} className="suggestion-subHeading">
                        <div className="p-r-2">
                          Retake the Wizard to select additional focus areas. You are seeing suggestions for{" "}
                        </div>
                        <Typography variant="body2" showBold color={tokens.Icon.General.Inverse}>
                          "
                        </Typography>
                        {focusedItem.length > 0 &&
                          focusedItem.map((data, i: number) => {
                            return (
                              <div className="inner-wrapper" key={uniqueId("ITEM_")}>
                                <Typography variant="body2" showBold color={tokens.Icon.General.Inverse}>
                                  {data}
                                </Typography>
                                {selectedFocusAreas?.length - 1 !== i && (
                                  <Typography
                                    variant="body2"
                                    showBold
                                    color={tokens.Icon.General.Inverse}
                                    className="ml-4"
                                  >
                                    /
                                  </Typography>
                                )}
                              </div>
                            );
                          })}
                        {selectedFocusAreas?.length > 3 && !isSeeAll && (
                          <Typography variant="body2" showBold color={tokens.Icon.General.Inverse} className="dots">
                            (...)
                          </Typography>
                        )}
                        <Typography variant="body2" showBold color={tokens.Icon.General.Inverse} className="dots">
                          "
                        </Typography>
                        <Typography variant="body2" showBold color={tokens.Icon.General.Inverse}>
                          .
                        </Typography>
                        {!isSeeAll && selectedFocusAreas?.length > 3 && (
                          <Typography
                            variant="body2"
                            showBold
                            color={tokens.Icon.General.Inverse}
                            className="see-more"
                            onClick={() => handleSetSeeAll()}
                          >
                            {translate("common.seeMore")}
                          </Typography>
                        )}
                      </Typography>
                    </div>
                    {isSeeAll && (
                      <Typography
                        variant="body2"
                        color={tokens.Icon.General.Inverse}
                        showBold
                        className="see-less"
                        onClick={() => handleSetSeeAll()}
                      >
                        {translate("common.seeLessUpper")}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography
                    showBold
                    variant="body1"
                    color={tokens.Icon.General.Inverse}
                    children="No focus areas selected"
                  />
                )}
              </div>
              <div className="right-container">
                <Button
                  accessType={accessTypes.delete}
                  variant="primaryWhite"
                  onClick={() => triggerOnboadingWizard(dispatch)}
                  label={"Retake the Wizard"}
                  size="Large"
                />
              </div>
            </div>
          ) : (
            <></>
          )}
          <Box marginBottom={"32px"}>
            {topSuggestedPlanIds.length ? (
              <>
                <TopSuggestedPlans />
                {!isEmpty(listIds) && (
                  <>
                    <Grid marginY={"24px"} marginX="8px">
                      <Typography variant={"h4"} showBold color={tokens.Text.General.PrimaryHeadline}>
                        All Suggested Plans
                      </Typography>
                    </Grid>
                    <SearchAndFilterRow
                      suggested
                      getInputDetails={getInputDetails}
                      isSearchBarVisible={true}
                      inputText={inputText}
                    />
                    <Plans
                      suggested
                      plansList={plansList}
                      isLoading={isLoading}
                      totalList={totalRecords}
                      loadMore={fetchMore}
                    ></Plans>
                  </>
                )}
              </>
            ) : (
              <NoPlansBanner height={isMobileScreen ? "270px" : "260px"} />
            )}
          </Box>
        </>
      )}
    </>
  );
}

export default Suggested;
